/**
 * useReturnChallans Hook
 * Manages return challan (Return to Vendor) operations for rejected items
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {
  ReturnChallan,
  ReturnChallanLine,
  ReturnChallanStatus,
  ReturnChallanFilters,
  CreateReturnChallanRequest,
  UpdateReturnChallanRequest,
  DispatchReturnChallanRequest,
} from '@/types/subcontracting';

export const useReturnChallans = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Generate unique return challan number
   */
  const generateReturnNumber = useCallback(async (): Promise<string> => {
    const today = new Date();
    const year = today.getFullYear().toString().slice(-2);
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const prefix = `RC${year}${month}`;

    // Get latest return number for this month
    const { data } = await supabase
      .from('return_challans')
      .select('return_number')
      .like('return_number', `${prefix}%`)
      .order('return_number', { ascending: false })
      .limit(1);

    let nextNumber = 1;
    if (data && data.length > 0) {
      const lastNumber = parseInt(data[0].return_number.slice(-4), 10);
      nextNumber = lastNumber + 1;
    }

    return `${prefix}${nextNumber.toString().padStart(4, '0')}`;
  }, []);

  /**
   * Fetch return challans with filters
   */
  const fetchReturnChallans = useCallback(async (
    filters?: ReturnChallanFilters
  ): Promise<ReturnChallan[]> => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('return_challans_with_details')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.vendor_id) {
        query = query.eq('vendor_id', filters.vendor_id);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.receipt_id) {
        query = query.eq('receipt_id', filters.receipt_id);
      }
      if (filters?.sub_contract_id) {
        query = query.eq('sub_contract_id', filters.sub_contract_id);
      }
      if (filters?.date_from) {
        query = query.gte('return_date', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('return_date', filters.date_to);
      }
      if (filters?.search_term) {
        query = query.or(`return_number.ilike.%${filters.search_term}%,vendor_name.ilike.%${filters.search_term}%`);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      return (data || []).map(rc => ({
        ...rc,
        lines: rc.lines || [],
      })) as ReturnChallan[];
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to fetch return challans: ${err.message}`,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Fetch a single return challan by ID
   */
  const fetchReturnChallanById = useCallback(async (id: string): Promise<ReturnChallan | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await supabase
        .from('return_challans_with_details')
        .select('*')
        .eq('id', id)
        .single();

      if (queryError) throw queryError;

      return {
        ...data,
        lines: data.lines || [],
      } as ReturnChallan;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new return challan
   */
  const createReturnChallan = useCallback(async (
    request: CreateReturnChallanRequest
  ): Promise<ReturnChallan | null> => {
    setLoading(true);
    setError(null);

    try {
      // Generate return number
      const return_number = await generateReturnNumber();

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Insert return challan
      const { data: returnChallan, error: insertError } = await supabase
        .from('return_challans')
        .insert({
          return_number,
          receipt_id: request.receipt_id,
          sub_contract_id: request.sub_contract_id,
          vendor_id: request.vendor_id,
          vendor_name: request.vendor_name,
          return_date: request.return_date || new Date().toISOString().split('T')[0],
          return_reason: request.return_reason,
          transport_mode: request.transport_mode,
          vehicle_number: request.vehicle_number,
          driver_name: request.driver_name,
          driver_contact: request.driver_contact,
          notes: request.notes,
          replacement_expected: request.replacement_expected || false,
          status: 'Draft',
          created_by: user?.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Insert return challan lines
      if (request.lines && request.lines.length > 0) {
        const linesToInsert = request.lines.map(line => ({
          return_challan_id: returnChallan.id,
          receipt_line_id: line.receipt_line_id,
          item_code: line.item_code,
          item_description: line.item_description,
          operation_name: line.operation_name,
          qty_rejected: line.qty_rejected,
          qty_returned: line.qty_returned,
          uom: line.uom,
          rejection_reason: line.rejection_reason,
          qc_remarks: line.qc_remarks,
          line_notes: line.line_notes,
        }));

        const { error: linesError } = await supabase
          .from('return_challan_lines')
          .insert(linesToInsert);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Return Challan Created',
        description: `Return Challan ${return_number} created successfully.`,
        variant: 'success',
      });

      return await fetchReturnChallanById(returnChallan.id);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to create return challan: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [generateReturnNumber, fetchReturnChallanById, toast]);

  /**
   * Update return challan
   */
  const updateReturnChallan = useCallback(async (
    id: string,
    request: UpdateReturnChallanRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('return_challans')
        .update({
          ...request,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Return challan updated successfully.',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to update return challan: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Dispatch return challan (mark as dispatched to vendor)
   */
  const dispatchReturnChallan = useCallback(async (
    request: DispatchReturnChallanRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      const { error: updateError } = await supabase
        .from('return_challans')
        .update({
          status: 'Dispatched',
          dispatch_date: new Date().toISOString(),
          dispatched_by: user?.id,
          transport_mode: request.transport_mode,
          vehicle_number: request.vehicle_number,
          driver_name: request.driver_name,
          driver_contact: request.driver_contact,
        })
        .eq('id', request.return_challan_id);

      if (updateError) throw updateError;

      toast({
        title: 'Return Challan Dispatched',
        description: 'Rejected items have been dispatched back to vendor.',
        variant: 'success',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to dispatch return challan: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Acknowledge return challan (vendor acknowledged receipt)
   */
  const acknowledgeReturnChallan = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('return_challans')
        .update({
          status: 'Acknowledged',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Vendor has acknowledged the return.',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to acknowledge return: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Mark as replaced (vendor sent replacement)
   */
  const markAsReplaced = useCallback(async (
    id: string,
    replacementDcId?: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('return_challans')
        .update({
          status: 'Replaced',
          replacement_dc_id: replacementDcId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Return marked as replaced.',
        variant: 'success',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to mark as replaced: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Mark as credited (credit note received)
   */
  const markAsCredited = useCallback(async (
    id: string,
    creditNoteNumber: string,
    creditNoteAmount: number
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('return_challans')
        .update({
          status: 'Credited',
          credit_note_number: creditNoteNumber,
          credit_note_amount: creditNoteAmount,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Credit note recorded successfully.',
        variant: 'success',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to record credit note: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Cancel return challan
   */
  const cancelReturnChallan = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('return_challans')
        .update({
          status: 'Cancelled',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Return challan cancelled.',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to cancel return challan: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Delete return challan (only if in Draft status)
   */
  const deleteReturnChallan = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Delete lines first (cascade should handle this but being explicit)
      await supabase
        .from('return_challan_lines')
        .delete()
        .eq('return_challan_id', id);

      const { error: deleteError } = await supabase
        .from('return_challans')
        .delete()
        .eq('id', id)
        .eq('status', 'Draft'); // Only allow deleting drafts

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'Return challan deleted.',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to delete return challan: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Get rejected lines from a receipt for creating return challan
   */
  const getReceiptRejectedLines = useCallback(async (receiptId: string) => {
    try {
      const { data, error } = await supabase
        .from('subcontract_receipt_lines')
        .select('*')
        .eq('receipt_id', receiptId)
        .gt('qty_received_reject', 0);

      if (error) throw error;

      return data || [];
    } catch (err: any) {
      console.error('Error fetching rejected lines:', err);
      return [];
    }
  }, []);

  return {
    loading,
    error,
    generateReturnNumber,
    fetchReturnChallans,
    fetchReturnChallanById,
    createReturnChallan,
    updateReturnChallan,
    dispatchReturnChallan,
    acknowledgeReturnChallan,
    markAsReplaced,
    markAsCredited,
    cancelReturnChallan,
    deleteReturnChallan,
    getReceiptRejectedLines,
  };
};

